import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useHistory } from "react-router-dom";
import "./AITickerDetail.scss";
import {
  DetailHomeIcon,
  DetailOptionIcon,
  DetailArrowBackIcon,
  TickerDetailChartIcon,
} from "../../common/Icon/AIAlerts";
import { FundamentalData } from "react-tradingview-embed";
import moment from "moment";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ExampleChart from "../../TradingView/ExampleChart";

import PieChartForSentiment from "../../company/PieChartForSentiment";
import NeedleImgLight from "../../../assets/images/needle_light.png";
import NeedleImgDark from "../../../assets/images/needle_dark.png";
import DiameterImgLight from "../../../assets/images/diameter_180_light.png";
import DiameterImgDark from "../../../assets/images/diameter_180_dark.png";
import { connect } from "react-redux";
import { companyDataLoad } from "../../../appRedux/ducks/companyData";
import { atsEquitiesLoad } from "../../../appRedux/ducks/atsEquities";
import { numberWithCommas } from "../../utilities";
import UnusualActivityTable from "../../common/Tables/UnusualActivityTable";
import { useAuth } from "../../../contexts/Auth";

// Helper functions for sentiment calculations
function categorizeSentiment(score) {
  if (score >= 76) {
    return "Very Bullish";
  } else if (score >= 51) {
    return "Bullish";
  } else if (score >= 26) {
    return "Slightly Bullish";
  } else if (score >= -25 && score < 26) {
    return "Neutral";
  } else if (score >= -50) {
    return "Slightly Bearish";
  } else if (score >= -75) {
    return "Bearish";
  } else {
    return "Very Bearish";
  }
}

function isInt(n) {
  return Number(n) === n && n % 1 === 0;
}

const AITickerDetail = ({
  ticker = "EOSE",
  alertText = "EOSE $11 Call 11/21",
  companyName = "Eos Energy Enterprises, Inc.",
  price = "330.19",
  priceChange = "+5.73",
  priceChangePercent = "+1.71",
  currency = "USD",
  activeTab = "Performance Summary",
  onTabChange,
  onBack,
  contractId,
  onBackToAlert,
  // Performance Summary data
  performanceDate = "Oct 24, 2024",
  closePrice = "330.19",
  openPrice = "335.92",
  performancePercent = "-1.71",
  dayRangeLow = "322.50",
  dayRangeHigh = "344.59",
  themeMode,
  companyData,
  companyDataLoading,
  fetchCompanyData,
  atsData,
  fetchAtsEquities,
  currentMarketStatus,
  userSettings,
}) => {
  const history = useHistory();
  const { hasAiOptionsAlertsNeural } = useAuth();
  // Fetch company data when ticker changes - only if user has Neural access
  useEffect(() => {
    if (ticker && hasAiOptionsAlertsNeural) {
      fetchCompanyData(ticker);
      fetchAtsEquities(ticker);
    }
  }, [ticker, fetchCompanyData, fetchAtsEquities, hasAiOptionsAlertsNeural]);

  // Handle sticky header after 96px scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition =
        window.scrollY || document.documentElement.scrollTop;
      setIsSticky(scrollPosition > 96);
    };

    window.addEventListener("scroll", handleScroll);
    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Local state for active tab
  const [currentActiveTab, setCurrentActiveTab] = useState(activeTab);
  const [newsIndex, setNewsIndex] = useState(0);

  // Sticky header scroll state
  const [isSticky, setIsSticky] = useState(false);
  // Real-time price tracking state
  const [lastPrice, setLastPrice] = useState("");
  const [tickerPriceHigh, setTickerPriceHigh] = useState({});
  const [tickerPriceLow, setTickerPriceLow] = useState({});

  // Unusual Activity state
  const [viewMode, setViewMode] = useState("15");
  const [sortModel, setSortModel] = useState([
    {
      field: "openTime",
      sort: "desc",
    },
  ]);

  // Sentiment time selector state
  const [sentimentTimeValue, setSentimentTimeValue] =
    useState("ai_scores_60min");

  // Data formatting functions for Unusual Activity
  const calcAvgPrice = (row) => {
    const totalPrice = row.totalPrice;
    const totalTrades = row.totalTrades;

    return `$${numberWithCommas(
      parseFloat(totalPrice / totalTrades).toFixed(2),
    )}`;
  };

  const calcTotalPrice = (row) => {
    const totalPrice = row.totalPrice;
    const totalTrades = row.totalTrades;
    const totalVolume = row.totalVolume;
    const avg = parseFloat(totalPrice / totalTrades);
    return `$${numberWithCommas(
      Math.trunc(parseFloat(avg * totalVolume).toFixed(2)),
    )}`;
  };

  // Extract ATS data
  const atsTableData = atsData?.table || [];
  const displayRows = useMemo(() => {
    return viewMode === "15" ? atsTableData.slice(0, 15) : atsTableData;
  }, [atsTableData, viewMode]);

  // Calculate percentage for volume indicator (matching UnusualActivity.js)
  const getPercentage = useCallback(
    (item) => {
      if (!atsTableData || atsTableData.length === 0) {
        return { width: "0%" };
      }
      const biggestNumber = Math.max.apply(
        Math,
        atsTableData.map(function (o) {
          return o.totalVolume;
        }),
      );
      const currentNumber = item.totalVolume;
      const perf =
        biggestNumber > 0
          ? ((currentNumber / biggestNumber) * 100).toFixed(2)
          : 0;
      return { width: `${perf}%` };
    },
    [atsTableData],
  );

  // Extract data from companyData if available
  const tickerDetails = companyData?.tickerDetails || {};
  const secPrice = companyData?.secPrice || {};
  const news = companyData?.news || [];
  const aiScores = companyData?.aiScores || [];
  const newsSentiment = companyData?.newsSentiment || 0;
  const isDataForCurrentTicker = tickerDetails?.ticker === ticker;

  // Reset lastPrice when ticker changes
  useEffect(() => {
    setLastPrice("");
    setTickerPriceHigh({});
    setTickerPriceLow({});
    setSentimentTimeValue("ai_scores_60min");
  }, [ticker]);

  // Market status logic matching TabHeaders2
  const marketStatus = useMemo(() => {
    if (currentMarketStatus === "open" && !secPrice?.open_price) {
      return "pre-open";
    }
    if (currentMarketStatus === "after" && !secPrice?.close_price) {
      return "open";
    }
    return currentMarketStatus || "closed";
  }, [currentMarketStatus, secPrice]);

  // OHLC calculation logic matching TabHeaders2 exactly
  const ohlc = useMemo(() => {
    const open = secPrice?.open_price || 0;
    const high = secPrice?.bounds?.high || 0;
    const low = secPrice?.bounds?.low || 0;
    const close =
      marketStatus === "after" || marketStatus === "closed"
        ? secPrice?.close_price || 0
        : secPrice?.prev_close_price || 0;

    let lastPriceUsed = {
      "pre-open": parseFloat(lastPrice || secPrice?.last_price || 0).toFixed(2),
      open: parseFloat(lastPrice || secPrice?.last_price || 0).toFixed(2),
      after: parseFloat(
        secPrice?.close_price
          ? secPrice?.close_price
          : lastPrice || secPrice?.last_price || 0,
      ).toFixed(2),
      closed: parseFloat(secPrice?.close_price || 0).toFixed(2),
    }[marketStatus];

    let compare = {
      "pre-open": parseFloat(
        secPrice?.pre_open || secPrice?.prev_close_price,
      ).toFixed(2),
      open: parseFloat(secPrice?.open_price).toFixed(2),
      after: parseFloat(secPrice?.open_price).toFixed(2),
      closed: parseFloat(secPrice?.open_price).toFixed(2),
    }[marketStatus];

    return {
      open,
      high,
      low,
      close,
      closeType: secPrice?.close_price ? "Close" : "Previous Close",
      performancePercent: (
        ((lastPriceUsed - compare) / compare) *
        100
      )?.toFixed(2),
      positivePerformance: ((lastPriceUsed - compare) / compare) * 100 > 0,
      secTicker: secPrice?.bounds?.ticker,
    };
  }, [JSON.stringify(secPrice), marketStatus, lastPrice]);

  // Track high price updates (matching TabHeaders2 logic)
  useEffect(() => {
    const keys = Object.keys(tickerPriceHigh);
    if (lastPrice >= ohlc.high && ticker === ohlc.secTicker && !!lastPrice) {
      if (!keys.length) {
        setTickerPriceHigh({
          [ohlc.secTicker]: lastPrice,
        });
      } else {
        if (tickerPriceHigh[ticker] < lastPrice) {
          setTickerPriceHigh({
            [ohlc.secTicker]: lastPrice,
          });
        }
      }
    }
  }, [ohlc, ticker, lastPrice]);

  // Track low price updates (matching TabHeaders2 logic)
  useEffect(() => {
    const keys = Object.keys(tickerPriceLow);
    if (lastPrice <= ohlc.low && ticker === ohlc.secTicker && !!lastPrice) {
      if (!keys.length) {
        setTickerPriceLow({
          [ohlc.secTicker]: lastPrice,
        });
      } else {
        if (tickerPriceLow[ticker] > lastPrice) {
          setTickerPriceLow({
            [ohlc.secTicker]: lastPrice,
          });
        }
      }
    }
  }, [ohlc, ticker, lastPrice]);

  // Calculate high and low to show (with dynamic tracking)
  const highToShow = useMemo(() => {
    const keys = Object.keys(tickerPriceHigh);
    if (
      !!keys.length &&
      keys[0] === ohlc.secTicker &&
      ohlc.high < tickerPriceHigh[ohlc.secTicker] &&
      ticker === keys[0]
    ) {
      return tickerPriceHigh[ohlc.secTicker];
    }
    return ohlc.high;
  }, [ticker, tickerPriceHigh, ohlc]);

  const lowToShow = useMemo(() => {
    const keys = Object.keys(tickerPriceLow);
    if (
      !!keys.length &&
      keys[0] === ohlc.secTicker &&
      ohlc.low > tickerPriceLow[ohlc.secTicker] &&
      ticker === keys[0]
    ) {
      return tickerPriceLow[ohlc.secTicker];
    }
    return ohlc.low;
  }, [ticker, tickerPriceLow, ohlc]);

  // Reset news index when ticker changes
  useEffect(() => {
    setNewsIndex(0);
  }, [ticker]);

  // Reset news index if it goes out of bounds when news data changes
  useEffect(() => {
    if (news && news.length > 0 && newsIndex >= news.length) {
      setNewsIndex(0);
    }
  }, [news, newsIndex]);

  // Format date matching TabHeaders2 format
  const formattedPerformanceDate = moment().format("MMMM Do, YYYY");

  // Calculate price performance matching CompanyPrice component logic exactly
  const performance = useMemo(() => {
    // Use default price if no real data available
    const fallbackPrice = parseFloat(price) || 0;

    let lastPriceUsed = {
      "pre-open": parseFloat(
        lastPrice || secPrice?.last_price || fallbackPrice,
      ).toFixed(2),
      open: parseFloat(
        lastPrice || secPrice?.last_price || fallbackPrice,
      ).toFixed(2),
      after: parseFloat(
        secPrice?.close_price ||
          lastPrice ||
          secPrice?.last_price ||
          fallbackPrice,
      ).toFixed(2),
      closed: parseFloat(
        secPrice?.close_price ||
          lastPrice ||
          secPrice?.last_price ||
          fallbackPrice,
      ).toFixed(2),
    }[marketStatus];

    let compare = {
      "pre-open": parseFloat(
        secPrice?.pre_open || secPrice?.prev_close_price || 0,
      ).toFixed(2),
      open: parseFloat(secPrice?.open_price || 0).toFixed(2),
      after: parseFloat(secPrice?.open_price || 0).toFixed(2),
      closed: parseFloat(secPrice?.open_price || 0).toFixed(2),
    }[marketStatus];

    const change = parseFloat(lastPriceUsed) - parseFloat(compare);
    const percent =
      compare > 0
        ? (
            ((parseFloat(lastPriceUsed) - parseFloat(compare)) /
              parseFloat(compare)) *
            100
          )?.toFixed(2)
        : "0.00";

    return {
      present: lastPriceUsed,
      change: change,
      percent: percent,
    };
  }, [marketStatus, secPrice, lastPrice, price]);

  // Format price change with sign (matching CompanyPrice display format)
  const formattedPriceChange = useMemo(() => {
    if (!isDataForCurrentTicker || !secPrice) {
      return priceChange;
    }
    return String(performance.percent).includes("-")
      ? performance.change.toFixed(2)
      : `+${performance.change.toFixed(2)}`;
  }, [isDataForCurrentTicker, secPrice, performance, priceChange]);

  // Format price change percent with sign (matching CompanyPrice display format)
  const formattedPriceChangePercent = useMemo(() => {
    if (!isDataForCurrentTicker || !secPrice) {
      return priceChangePercent;
    }
    return String(performance.percent).includes("-")
      ? performance.percent
      : `+${performance.percent}`;
  }, [isDataForCurrentTicker, secPrice, performance, priceChangePercent]);

  // Use real data if available and matches current ticker, otherwise use defaults
  const displayTicker = isDataForCurrentTicker
    ? tickerDetails.ticker || ticker
    : ticker;
  const displayCompanyName = isDataForCurrentTicker
    ? tickerDetails.name || companyName
    : companyName;
  const displayPrice =
    isDataForCurrentTicker && secPrice ? performance.present : price;
  const displayCurrency = isDataForCurrentTicker
    ? (tickerDetails.currency_name || currency).toUpperCase()
    : currency;

  // Performance Summary data using ohlc calculations (matching TabHeaders2)
  const displayClosePrice =
    isDataForCurrentTicker && ohlc.close
      ? Number(ohlc.close).toFixed(2)
      : closePrice;
  const displayOpenPrice =
    isDataForCurrentTicker && ohlc.open
      ? Number(ohlc.open).toFixed(2)
      : openPrice;
  const displayPerformancePercent =
    isDataForCurrentTicker && ohlc.performancePercent
      ? `${ohlc.positivePerformance ? "+" : ""}${Number(ohlc.performancePercent).toFixed(2)}`
      : performancePercent;
  const displayDayRangeLow =
    isDataForCurrentTicker && lowToShow
      ? Number(lowToShow).toFixed(2)
      : dayRangeLow;
  const displayDayRangeHigh =
    isDataForCurrentTicker && highToShow
      ? Number(highToShow).toFixed(2)
      : dayRangeHigh;

  // About section data
  const displayDescription = isDataForCurrentTicker
    ? tickerDetails.description || ""
    : "";
  const displaySector = isDataForCurrentTicker
    ? tickerDetails.sector || ""
    : "";
  const displayIndustry = isDataForCurrentTicker
    ? tickerDetails.industry || ""
    : "";
  const displayEmployees =
    isDataForCurrentTicker && tickerDetails.total_employees
      ? numberWithCommas(tickerDetails.total_employees)
      : "";
  const displayWebsite = isDataForCurrentTicker
    ? tickerDetails.homepage_url || ""
    : "";

  // Check if we have about data
  const hasAboutData =
    isDataForCurrentTicker &&
    (displayDescription ||
      displaySector ||
      displayIndustry ||
      displayEmployees ||
      displayWebsite);

  // Calculate AI sentiment data
  const aiScore = useMemo(() => {
    return (
      aiScores?.find((item) => item?.table_name === sentimentTimeValue) || {
        sentiment: "",
      }
    );
  }, [aiScores, sentimentTimeValue]);

  // Check if AI data exists - verify sentiment is not empty and scores are valid numbers
  const hasAiData = !!(
    aiScore?.sentiment?.length &&
    aiScore?.bull_score !== "" &&
    aiScore?.bear_score !== "" &&
    !isNaN(parseFloat(aiScore?.bull_score)) &&
    !isNaN(parseFloat(aiScore?.bear_score))
  );

  const sentimentLabel = hasAiData
    ? categorizeSentiment(aiScore.ai_score)
    : "Neutral";

  const bullScore =
    hasAiData && aiScore?.bull_score !== ""
      ? parseFloat(aiScore.bull_score)
      : 0;
  const bearScore =
    hasAiData && aiScore?.bear_score !== ""
      ? parseFloat(aiScore.bear_score)
      : 0;

  // Filter tabs based on available data
  const allTabs = [
    "Performance Summary",
    "Chart",
    "Financials",
    "About",
    "Sentiments",
    "News",
    "Options Overview",
    "Unusual Activity",
  ];
  const tabs = allTabs.filter((tab) => {
    // If user doesn't have Neural access, only show basic tabs
    if (!hasAiOptionsAlertsNeural) {
      return (
        tab === "Performance Summary" || tab === "Chart" || tab === "Financials"
      );
    }
    // Otherwise, filter based on available data
    if (tab === "About" && !hasAboutData) return false;
    if (tab === "News" && (!news || news.length === 0)) return false;
    if (tab === "Sentiments" && !hasAiData) return false;
    return true;
  });

  // News navigation handlers
  const handlePrevNews = useCallback(() => {
    setNewsIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextNews = useCallback(() => {
    setNewsIndex((prev) => {
      if (!news || news.length === 0) return prev;
      return Math.min(news.length - 1, prev + 1);
    });
  }, [news]);

  // Get current news item - memoized to prevent unnecessary recalculations
  const currentNews = useMemo(() => {
    return news && news.length > 0 && newsIndex < news.length
      ? news[newsIndex]
      : null;
  }, [news, newsIndex]);

  const canNavigatePrev = useMemo(() => newsIndex > 0, [newsIndex]);
  const canNavigateNext = useMemo(
    () => newsIndex < news.length - 1,
    [newsIndex, news.length],
  );

  // Calculate news sentiment data
  const numericNewsSentiment = Number(newsSentiment);
  const numericNewsSentimentLabel =
    numericNewsSentiment > 0
      ? "+" +
        (isInt(numericNewsSentiment) === true
          ? numericNewsSentiment
          : numericNewsSentiment.toFixed(1))
      : isInt(numericNewsSentiment) === true
        ? numericNewsSentiment
        : numericNewsSentiment.toFixed(1);

  const numericNewsSentimentAngle = useMemo(() => {
    return (numericNewsSentiment * 120) / 100;
  }, [numericNewsSentiment]);

  // Refs for smooth scrolling
  const sectionRefs = useRef({
    "Performance Summary": useRef(null),
    Chart: useRef(null),
    Financials: useRef(null),
    About: useRef(null),
    Sentiments: useRef(null),
    News: useRef(null),
    "Options Overview": useRef(null),
    "Unusual Activity": useRef(null),
  });

  // Ref to track programmatic scrolling state to prevent conflicts with scroll spy
  const isScrollingProgrammatically = useRef(false);

  // Determine if price change is positive or negative (matching CompanyPrice logic)
  const isPositive = useMemo(() => {
    if (!isDataForCurrentTicker || !secPrice) {
      return (
        formattedPriceChange?.toString().startsWith("+") ||
        parseFloat(formattedPriceChange) > 0
      );
    }
    return !String(performance.percent).includes("-");
  }, [isDataForCurrentTicker, secPrice, performance, formattedPriceChange]);
  const priceChangeClass = isPositive ? "positive" : "negative";

  // Determine if performance is positive or negative using ohlc calculation
  const isPerfPositive =
    isDataForCurrentTicker && ohlc.positivePerformance !== undefined
      ? ohlc.positivePerformance
      : displayPerformancePercent?.toString().startsWith("+") ||
        parseFloat(displayPerformancePercent) > 0;
  const perfChangeClass = isPerfPositive ? "positive" : "negative";

  // Memoize FundamentalData widget to prevent re-renders
  const memoizedFundamentalData = useMemo(
    () =>
      displayTicker ? (
        <FundamentalData
          className="hide-scroll"
          widgetProps={{
            symbol: displayTicker,
            colorTheme: themeMode,
            width: "100%",
            isTransparent: themeMode === "light",
          }}
        />
      ) : null,
    [themeMode, displayTicker],
  );

  const handleTabClick = (tab) => {
    // Set flag to prevent scroll spy from updating during programmatic scroll
    isScrollingProgrammatically.current = true;

    // Update local active tab state
    setCurrentActiveTab(tab);

    // Call parent callback if provided
    if (onTabChange) {
      onTabChange(tab);
    }

    // Smooth scroll to the section with offset for sticky header
    const sectionRef = sectionRefs.current[tab];
    if (sectionRef?.current) {
      const elementTop = sectionRef.current.offsetTop;
      const offsetPosition = elementTop - 196; // Account for sticky header height

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      // Reset flag after scroll animation completes (typically 500-800ms)
      // Using scrollend event if available, otherwise setTimeout fallback
      let scrollEndTimeout;
      const handleScrollEnd = () => {
        isScrollingProgrammatically.current = false;
        window.removeEventListener("scrollend", handleScrollEnd);
        if (scrollEndTimeout) clearTimeout(scrollEndTimeout);
      };

      // Try scrollend event first (modern browsers)
      if ("onscrollend" in window) {
        window.addEventListener("scrollend", handleScrollEnd, { once: true });
      } else {
        // Fallback: setTimeout for older browsers
        scrollEndTimeout = setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 800);
      }
    } else {
      // If section doesn't exist, reset flag immediately
      isScrollingProgrammatically.current = false;
    }
  };

  const handleNavigateHome = (e) => {
    e.preventDefault();
    if (onBack) {
      onBack();
    } else {
      history.push("/ai-options-alerts/live");
    }
  };

  const handleNavigateToAlert = (e) => {
    e.preventDefault();
    if (onBackToAlert) {
      onBackToAlert();
    } else if (contractId) {
      // Fallback to navigation if callback not provided
      history.push(`/ai-options-alerts/trade/${contractId}`);
    }
  };

  const handleNavigateToOptionChain = (e) => {
    e.preventDefault();
    history.push(`/option-chain/${displayTicker}`);
  };

  // Scroll spy: Update active tab based on visible section
  useEffect(() => {
    // Get all available sections (only those that exist in tabs and have refs)
    const availableSections = tabs.filter((tab) => {
      const ref = sectionRefs.current[tab];
      return ref?.current !== null;
    });

    if (availableSections.length === 0) return;

    // Handler to determine which section is closest to viewport center
    const handleIntersection = () => {
      // Don't update if programmatic scrolling is in progress
      if (isScrollingProgrammatically.current) return;

      // Calculate viewport center - account for sticky header (196px at top)
      // The center should be relative to the visible content area
      const stickyHeaderHeight = isSticky ? 196 : 0;
      const contentAreaTop = stickyHeaderHeight;
      const contentAreaHeight = window.innerHeight - stickyHeaderHeight;
      const viewportCenter =
        window.scrollY + contentAreaTop + contentAreaHeight / 2;

      // Check if we're at the very top of the page (for Performance Summary)
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const isAtVeryTop = scrollTop < 200; // Stricter threshold for "at the top"
      const firstSection = availableSections[0]; // Performance Summary

      // When at the very top, prioritize Performance Summary
      if (isAtVeryTop && firstSection) {
        const firstSectionRef = sectionRefs.current[firstSection];
        if (firstSectionRef?.current) {
          const rect = firstSectionRef.current.getBoundingClientRect();
          const isVisible =
            rect.bottom > stickyHeaderHeight &&
            rect.top < window.innerHeight &&
            rect.height > 0;

          // If Performance Summary is visible and we're at the very top, select it
          if (isVisible && firstSection !== currentActiveTab) {
            setCurrentActiveTab(firstSection);
            if (onTabChange) {
              onTabChange(firstSection);
            }
            return; // Exit early to prevent other sections from being considered
          }
          // If Performance Summary is visible and already selected, don't check other sections
          if (isVisible && firstSection === currentActiveTab) {
            return; // Exit early to prevent other sections from being considered
          }
        }
      }

      // For all other cases, find the closest section to viewport center
      let closestSection = null;
      let minDistance = Infinity;

      availableSections.forEach((tab) => {
        const ref = sectionRefs.current[tab];
        if (ref?.current) {
          const rect = ref.current.getBoundingClientRect();

          // Skip Performance Summary if we're not at the very top
          if (tab === firstSection && !isAtVeryTop) {
            return;
          }

          // Calculate section center relative to document
          const sectionTop = rect.top + window.scrollY;
          const sectionCenter = sectionTop + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          // Only consider sections that are at least partially visible
          const isVisible =
            rect.bottom > stickyHeaderHeight &&
            rect.top < window.innerHeight &&
            rect.height > 0;

          if (isVisible && distance < minDistance) {
            minDistance = distance;
            closestSection = tab;
          }
        }
      });

      // Update active tab if we found a closest section
      if (
        closestSection &&
        closestSection !== currentActiveTab &&
        minDistance < window.innerHeight * 2
      ) {
        setCurrentActiveTab(closestSection);
        if (onTabChange) {
          onTabChange(closestSection);
        }
      }
    };

    // Use scroll event instead of IntersectionObserver for more reliable detection
    // Check on scroll and also set up an interval to check periodically
    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        handleIntersection([]);
      }, 100);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Initial check
    handleIntersection([]);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [
    tabs,
    currentActiveTab,
    onTabChange,
    hasAiOptionsAlertsNeural,
    hasAboutData,
    hasAiData,
    news,
    isSticky,
  ]);

  return (
    <div className="ai-ticker-detail-container">
      {/* Sticky Header Container */}
      <div
        className={`ai-ticker-detail-sticky-header ${isSticky ? "sticky-active" : ""}`}
      >
        {/* Section 1: Breadcrumb Navigation */}
        <div className="ai-ticker-detail-breadcrumb">
          <div className="breadcrumb-container">
            <div className="breadcrumb-content">
              {/* Home Link */}
              <div className="breadcrumb-link-wrapper">
                <a
                  href="/ai-options-alerts/live"
                  className="breadcrumb-link"
                  onClick={handleNavigateHome}
                >
                  <div className="breadcrumb-icon">
                    <DetailHomeIcon />
                  </div>
                  <div className="breadcrumb-text">
                    <p>AI Options Alerts</p>
                  </div>
                </a>
              </div>

              {/* Separator */}
              <div className="breadcrumb-separator">
                <p>/</p>
              </div>

              {/* Alert Link */}
              <div className="breadcrumb-link-wrapper">
                <a
                  href="#"
                  className="breadcrumb-link"
                  onClick={handleNavigateToAlert}
                >
                  <div className="breadcrumb-icon">
                    <DetailOptionIcon />
                  </div>
                  <div className="breadcrumb-text">
                    <p>{alertText}</p>
                  </div>
                </a>
              </div>

              {/* Separator */}
              <div className="breadcrumb-separator">
                <p>/</p>
              </div>

              {/* Current Ticker */}
              <div className="breadcrumb-link-wrapper">
                <div className="breadcrumb-current">
                  <div className="breadcrumb-icon">
                    <TickerDetailChartIcon />
                  </div>
                  <div className="breadcrumb-text">
                    <p>{displayTicker}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Ticker Overview Header */}
        <div className="ai-ticker-overview">
          <div className="overview-container">
            <div className="overview-content">
              {/* Back Button */}
              <button
                className="back-button"
                onClick={onBackToAlert || onBack || (() => history.goBack())}
              >
                <div className="back-icon">
                  <DetailArrowBackIcon />
                </div>
              </button>

              {/* Header Content */}
              <div className="header-section">
                <div className="header-content">
                  {/* Ticker Symbol */}
                  <p className="ticker-symbol">{displayTicker}</p>

                  {/* Company Name */}
                  <p className="company-name">{displayCompanyName}</p>

                  {/* Price Section */}
                  <div className="price-section">
                    {/* Current Price */}
                    <div className="price-display">
                      <p className="price-value">${displayPrice}</p>
                      <p className="price-currency">{displayCurrency}</p>
                    </div>

                    {/* Price Change */}
                    <div className="price-change">
                      <p className={`change-value ${priceChangeClass}`}>
                        ({formattedPriceChange})
                      </p>
                      <div className={`change-chip ${priceChangeClass}`}>
                        <p className="change-percent">
                          {formattedPriceChangePercent}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: Tabs Navigation */}
        <div className="ai-ticker-tabs">
          <div className="tabs-container">
            {tabs.map((tab) => {
              const isActive = tab === currentActiveTab;
              return (
                <button
                  key={tab}
                  className={`tab-item ${isActive ? "active" : ""}`}
                  onClick={() => handleTabClick(tab)}
                >
                  <div className="tab-content">
                    <p className="tab-text">{tab}</p>
                  </div>
                  {isActive && <div className="tab-indicator" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="ai-ticker-detail-main-content">
        <div
          ref={sectionRefs.current["Performance Summary"]}
          className="performance-summary-card"
        >
          {/* Header */}
          <div className="performance-header">
            <p className="performance-title">Performance Summary</p>
            <p className="performance-date">{formattedPerformanceDate}</p>
          </div>

          {/* Summary Data */}
          <div className="performance-data">
            {/* Close */}
            <div className="data-item">
              <p className="data-label">
                {isDataForCurrentTicker && ohlc.closeType
                  ? ohlc.closeType
                  : "Close"}
              </p>
              <p className="data-value">${displayClosePrice}</p>
            </div>

            {/* Open */}
            <div className="data-item">
              <p className="data-label">Open</p>
              <p className="data-value">${displayOpenPrice}</p>
            </div>

            {/* Performance */}
            <div className="data-item">
              <p className="data-label">Performance</p>
              <div className={`performance-chip ${perfChangeClass}`}>
                <p className="chip-text">{displayPerformancePercent}%</p>
              </div>
            </div>

            {/* Day Range */}
            <div className="data-item">
              <p className="data-label">Day Range</p>
              <p className="data-value">
                ${displayDayRangeLow} – ${displayDayRangeHigh}
              </p>
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div ref={sectionRefs.current["Chart"]} className="chart-wrapper">
          {displayTicker && (
            <ExampleChart
              symbol={displayTicker}
              themeMode={themeMode}
              userSettings={userSettings}
            />
          )}
        </div>

        <div ref={sectionRefs.current["Financials"]}>
          {memoizedFundamentalData}
        </div>

        {/* About Section */}
        {hasAiOptionsAlertsNeural && hasAboutData && (
          <div ref={sectionRefs.current["About"]} className="about-card">
            <p className="about-title">About</p>
            {displayDescription && (
              <p className="about-description">{displayDescription}</p>
            )}
            {(displaySector ||
              displayIndustry ||
              displayEmployees ||
              displayWebsite) && (
              <>
                <div className="about-divider"></div>
                <div className="about-info">
                  {displaySector && (
                    <div className="info-item">
                      <p className="info-label">Sector</p>
                      <p className="info-value">{displaySector}</p>
                    </div>
                  )}
                  {displayIndustry && (
                    <div className="info-item">
                      <p className="info-label">Industry</p>
                      <p className="info-value">{displayIndustry}</p>
                    </div>
                  )}
                  {displayEmployees && (
                    <div className="info-item">
                      <p className="info-label">Employees</p>
                      <p className="info-value">{displayEmployees}</p>
                    </div>
                  )}
                  {displayWebsite && (
                    <div className="info-item">
                      <p className="info-label">Website</p>
                      <p className="info-value website-link">
                        {displayWebsite}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Sentiments Section */}
        {hasAiOptionsAlertsNeural && hasAiData && (
          <div
            ref={sectionRefs.current["Sentiments"]}
            className="news-sentiment-container"
          >
            <div className="news-sentiment-title">Sentiments</div>
            <div className="ai-ticker-news-sentiment-timestamp">
              <div
                className={`ai-ticker-news-sentiment-timevalue ${sentimentTimeValue === "ai_scores_60min" ? "active" : ""}`}
                onClick={() => setSentimentTimeValue("ai_scores_60min")}
              >
                1 Hour
              </div>
              <div
                className={`ai-ticker-news-sentiment-timevalue ${sentimentTimeValue === "ai_scores_4h" ? "active" : ""}`}
                onClick={() => setSentimentTimeValue("ai_scores_4h")}
              >
                4 Hour
              </div>
              <div
                className={`ai-ticker-news-sentiment-timevalue ${sentimentTimeValue === "ai_scores_24h" ? "active" : ""}`}
                onClick={() => setSentimentTimeValue("ai_scores_24h")}
              >
                1 Day
              </div>
            </div>
            <div className="news-sentiment-charts ai-ticker-detail-sentiment-charts">
              <div className="news-ai-sentiment-chart">
                <div className="news-ai-sentiment-chart-title">
                  A.I. Sentiment
                </div>
                <PieChartForSentiment
                  data={[bullScore, bearScore]}
                  percentage={Math.max(bullScore, bearScore)}
                  label={sentimentLabel}
                />
              </div>
              <div className="news-sentiment-details">
                <div className="news-sentiment-details-title">
                  News Sentiment
                </div>

                <div
                  className={`news-sentiment-details-number ${numericNewsSentiment > 0 ? "positive" : "negative"}`}
                >
                  {numericNewsSentimentLabel}
                </div>

                <div className="tickers-info__news-sentiment-details-content">
                  <img
                    src={
                      themeMode === "light" ? DiameterImgLight : DiameterImgDark
                    }
                    alt="Diameter"
                    className="tickers-info__diameter-img"
                  />
                  <div className="tickers-info__needle-img-wrapper">
                    <img
                      src={
                        themeMode === "light" ? NeedleImgLight : NeedleImgDark
                      }
                      alt="Needle"
                      className="tickers-info__needle-img"
                      style={{
                        transform: `rotate(${numericNewsSentimentAngle}deg)`,
                      }}
                    />
                  </div>
                </div>

                <div className="news-sentiment-details-label">
                  Sentiment Score
                </div>
                <div
                  className={`news-sentiment-details-value ${numericNewsSentiment > 0 ? "positive" : "negative"}`}
                >
                  {numericNewsSentimentLabel}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Latest News Section */}
        {hasAiOptionsAlertsNeural && news && news.length > 0 && (
          <div ref={sectionRefs.current["News"]} className="latest-news-card">
            {/* Header */}
            <div className="news-header">
              <p className="news-title">Latest News</p>
              {news.length > 1 && (
                <div className="news-navigation">
                  <button
                    className="nav-button nav-left"
                    onClick={handlePrevNews}
                    disabled={!canNavigatePrev}
                    style={{
                      opacity: canNavigatePrev ? 1 : 0.5,
                      cursor: canNavigatePrev ? "pointer" : "not-allowed",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0C4.48 0 0 4.48 0 10ZM10 7.21V9H13C13.55 9 14 9.45 14 10C14 10.55 13.55 11 13 11H10V12.79C10 13.24 9.46 13.46 9.15 13.14L6.36 10.35C6.16 10.15 6.16 9.84 6.36 9.64L9.15 6.85C9.46 6.54 10 6.76 10 7.21Z"
                        fill="black"
                        fillOpacity="0.38"
                      />
                    </svg>
                  </button>
                  <button
                    className="nav-button nav-right"
                    onClick={handleNextNews}
                    disabled={!canNavigateNext}
                    style={{
                      opacity: canNavigateNext ? 1 : 0.5,
                      cursor: canNavigateNext ? "pointer" : "not-allowed",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M20 10C20 4.48 15.52 0 10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10ZM10 12.79V11H7C6.45 11 6 10.55 6 10C6 9.45 6.45 9 7 9H10V7.21C10 6.76 10.54 6.54 10.85 6.86L13.64 9.65C13.84 9.85 13.84 10.16 13.64 10.36L10.85 13.15C10.54 13.46 10 13.24 10 12.79Z"
                        fill="black"
                        fillOpacity="0.56"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Article */}
            <div className="news-article-wrapper">
              {currentNews ? (
                <div className="news-article-content">
                  <div className="news-article">
                    <div className="article-header">
                      <p className="article-title">{currentNews.title || ""}</p>
                      {currentNews.published_utc && (
                        <p className="article-date">
                          Published on{" "}
                          {moment(currentNews.published_utc).format(
                            "MMM D YYYY",
                          )}
                        </p>
                      )}
                    </div>
                    <p className="article-excerpt">
                      {currentNews.description || ""}
                    </p>
                  </div>

                  {/* Read More Button */}
                  {currentNews.article_url && (
                    <a
                      href={currentNews.article_url}
                      target="_blank"
                      rel="noreferrer"
                      className="read-more-button"
                      style={{
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                    >
                      <span>Read more</span>
                    </a>
                  )}
                </div>
              ) : (
                <div className="news-article-content">
                  <p>No news available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Options Overview Section */}
        {hasAiOptionsAlertsNeural && (
          <div
            ref={sectionRefs.current["Options Overview"]}
            className="options-overview-card"
          >
            {/* Header */}
            <div className="options-overview-header">
              <p className="options-overview-title">Options Overview</p>
            </div>

            {/* View Options Chain Button */}
            <div className="options-overview-content">
              <button
                className="view-options-chain-button"
                onClick={handleNavigateToOptionChain}
              >
                <QueryStatsRoundedIcon />
                <span>View Options Chain</span>
              </button>
            </div>
          </div>
        )}

        {/* Unusual Activity Section */}
        {hasAiOptionsAlertsNeural && (
          <div
            ref={sectionRefs.current["Unusual Activity"]}
            className="unusual-activity-section"
          >
            {/* Header with Title and Toggle */}
            <div className="activity-header">
              <p className="activity-title">Unusual Activity</p>
              <div className="toggle-button-group">
                <button
                  className={`toggle-button ${viewMode === "15" ? "active" : ""}`}
                  onClick={() => setViewMode("15")}
                >
                  View 15
                </button>
                <button
                  className={`toggle-button ${viewMode === "all" ? "active" : ""}`}
                  onClick={() => setViewMode("all")}
                >
                  View all
                </button>
              </div>
            </div>

            {/* Table Container */}
            {displayRows.length > 0 ? (
              <div className="unusual-activity-table-container">
                <UnusualActivityTable
                  calcAvgPrice={calcAvgPrice}
                  getPercentage={getPercentage}
                  calcTotalPrice={calcTotalPrice}
                  data={displayRows}
                  rangeEnabled={false}
                  sortModel={sortModel}
                  setSortModel={setSortModel}
                />
              </div>
            ) : (
              <div className="unusual-activity-no-data">
                <p>No unusual activity data available for this ticker</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
  companyData: state.companyData.companyData,
  companyDataLoading: state.companyData.companyDataLoading,
  atsData: state.ats,
  currentMarketStatus: state.companyData.companyData.marketState,
  userSettings: state.userSettings.userSettings,
});

const dispatchToProps = (dispatch) => ({
  fetchCompanyData: (symbol) => dispatch(companyDataLoad(symbol)),
  fetchAtsEquities: (ticker) => dispatch(atsEquitiesLoad(ticker)),
});

export default connect(stateToProps, dispatchToProps)(AITickerDetail);
